import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  MenuItem,
  Button,
  DialogActions,
  Typography,
  Box,
  IconButton,
  Grid,
  Tooltip,
  InputAdornment
} from "@material-ui/core";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { SITE_KEY } from '../../../constants';
import ReCAPTCHA from 'react-google-recaptcha';
import { errorAlert } from '../../../utils/alerts/alerts';
import { postInfo } from '../../../utils/backendRequest';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import { useDispatch, useSelector } from "react-redux";
import { setContract, setDebt } from '../../../reducers/debtReducer';
import axios from 'axios';
import api from '../../../utils/axios';

const useStyles = makeStyles({
  roundedInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
    },
  },

  tooltipTransparent: {
    backgroundColor: "transparent",
    boxShadow: "none",
    border: "none",
    padding: 0,
    margin: 0,
  },

  popperNoPadding: {
    padding: 0,
    margin: 0,
    "&[x-placement*='bottom'] $tooltipTransparent": {
      marginTop: 0,
    },
  },

});

const validationSchema = Yup.object({
  contrato: Yup.string()
    .matches(/^[0-9]+$/, 'Solo se permiten números')
    .length(7, 'Debe tener 7 dígitos')
    .required('Campo obligatorio'),
  tipoDocumento: Yup.string()
    .required("Campo requerido"),
  numeroDocumento: Yup.string()
    .matches(/^[0-9]+$/, 'Solo se permiten números')
    .min(6, 'Debe tener al menos 6 dígitos')
    .max(8, 'No puede tener más de 8 dígitos')
    .required('Campo obligatorio'),
});

const OnlinePaymentModal = ({ open, close, success }) => {
  const [isLoading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const recaptchaRef = useRef(null);
  const classes = useStyles();
  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      tipoDocumento: '',
      numeroDocumento: '',
      contrato: '',
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      setErrorMessage('');
      setLoading(true);
      try {
        localStorage.setItem("OnlinePaymentModalView", "true");

        if (!recaptchaRef.current) {
          throw new Error("reCAPTCHA no está inicializado.");
        }

        const tokenRc = await recaptchaRef.current.executeAsync();
        recaptchaRef.current.reset();

        if (!tokenRc) {
          throw new Error("Token reCAPTCHA inválido.");
        }

        const payload = {
          tipoDocumento: values.tipoDocumento,
          numeroDocumento: values.numeroDocumento,
          contrato: values.contrato,
          prepaga: 4,
          fecha: moment().format()
        };

        const response = await api.post(
          `/api/consultDebtWithoutLogin`,
          payload,
          {
            headers: {
              recaptcha: tokenRc,
            },
            withCredentials: true,
          }
        );


        const contratos = response.data?.contratos || response.data;
        let personaTitular;
        
        if (Array.isArray(contratos)) {                      // Si es un array son varios contratos
            personaTitular =  response.data.persona.contra; 
        } else {
            personaTitular = response.data;
        }

        sessionStorage.setItem('contractSelected', personaTitular.toString());
        dispatch(setContract(personaTitular));
        success();

       
      } catch (error) {
        console.error("Ha ocurrido un error:", error.response.data.message);
        setErrorMessage(error.response.data.message);
        //errorAlert("Tuvimos un problema", "Volve a intentarlo en unos minutos por favor.");
      } finally {
        setLoading(false);
      }
    }
  });

  const isButtonDisabled =
    isLoading ||
    formik.values.tipoDocumento.trim() === "" ||
    formik.values.numeroDocumento.trim() === "" ||
    formik.values.contrato.trim() === "" ||
    formik.isValid !== true;

  return (
    <>
      <Dialog
        open={open}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 20,
          },
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          style={{ padding: '16px 24px 0 24px' }}
        >
          <Typography variant="h6" style={{ fontWeight: 500 }}>
            Pago Online
          </Typography>
          <IconButton
            onClick={() => {
              formik.resetForm();
              close();
              setErrorMessage('');
            }}
            size="small"
            style={{ padding: 4 }}
          >
            <i className="fa fa-times" aria-hidden="true"></i>
          </IconButton>
        </Box>


        <DialogContent>
          <Typography variant="body2" style={{ fontWeight: 350 }}>
            Completa tus datos para iniciar el pago.
          </Typography>

          <Box style={{ paddingTop: 10 }} component="form" noValidate autoComplete="off" onSubmit={formik.handleSubmit}>

            <TextField
              select
              fullWidth
              label="Tipo de documento"
              id="tipoDocumento"
              name="tipoDocumento"
              value={formik.values.tipoDocumento}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.tipoDocumento && Boolean(formik.errors.tipoDocumento)}
              helperText={formik.touched.tipoDocumento && formik.errors.tipoDocumento}
              margin="normal"
              variant="outlined"
              className={classes.roundedInput}>

              <MenuItem value="">
                <em>Seleccionar</em>
              </MenuItem>
              <MenuItem value={"DU"}>DU</MenuItem>
              <MenuItem value={"Pasaporte"}>Pasaporte</MenuItem>
            </TextField>
            

            <TextField
              fullWidth
              label="N° de Documento"
              name="numeroDocumento"
              value={formik.values.numeroDocumento}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.numeroDocumento && Boolean(formik.errors.numeroDocumento)}
              helperText={formik.touched.numeroDocumento && formik.errors.numeroDocumento}
              margin="normal"
              variant="outlined"
              className={classes.roundedInput}
            />

            <TextField
              fullWidth
              label="N° de Asociado"
              name="contrato"
              value={formik.values.contrato}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.contrato && Boolean(formik.errors.contrato)}
              helperText={formik.touched.contrato && formik.errors.contrato}
              margin="normal"
              variant="outlined"
              className={classes.roundedInput}

              InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {/* <Tooltip title="Podés encontrar tu número de contrato en tu factura o credencial."> */}
                        {/* <HelpOutlineIcon color="action" /> */}

                        <Tooltip title={
                            <React.Fragment>
                                <div style={{ textAlign: "center" }}>
                                  <img
                                    src="/img/nro_asociado.png"
                                    alt="Nro. asociado"
                                    style={{
                                      width: 220,
                                      borderRadius: 8,
                                      display: "block",
                                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
                                    }}
                                  />
                                </div>
                              </React.Fragment>
                            } interactive 
                              placement="right" 
                              classes={{
                                tooltip: classes.tooltipTransparent,
                                popper: classes.popperNoPadding,
                              }}
                            >

                            <IconButton size="small">
                              <HelpOutlineIcon fontSize="small" />
                            </IconButton>  
                        </Tooltip>
                    </InputAdornment>
                  ),
              }}
            />

            {errorMessage && (
              <Typography
                variant="body2"
                style={{
                  color: 'red',
                  marginTop: 8,
                  fontWeight: 500,
                }}
              >
                {errorMessage}
              </Typography>
            )}

            <DialogActions>
              <Button
                type="submit"
                style={{
                  marginTop: 16,
                  width: 110,
                  height: 40,
                  fontSize: 14,
                  textTransform: 'none',
                  borderRadius: 20,
                  backgroundColor: !formik.values.numeroDocumento ||
                    !formik.values.contrato ||
                    !formik.isValid ? '#bdbdbd' : '#00748b',
                  color: '#ffffff',
                  cursor: !formik.values.numeroDocumento ||
                    !formik.values.contrato ||
                    !formik.isValid ? 'not-allowed' : 'pointer',
                  border: 'none',
                  boxShadow: 'none'
                }}
                variant="contained"
                disabled={isButtonDisabled}

              >
                Continuar
              </Button>

            </DialogActions>
            <ReCAPTCHA
              ref={recaptchaRef}
              style={{ marginTop: "5%" }}
              size="invisible"
              sitekey={SITE_KEY}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OnlinePaymentModal;
